import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { formatGBP, formatDate } from "@/lib/utils-cart";
import { toast } from "sonner";
import { Tag, MessageSquare, X } from "lucide-react";

export default function CRM() {
  const [customers, setCustomers] = useState([]);
  const [segments, setSegments] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newSegment, setNewSegment] = useState({ name: "", description: "" });
  const [tags, setTags] = useState("");
  const [note, setNote] = useState("");

  const load = () => {
    api.get("/crm/customers").then((r) => setCustomers(r.data));
    api.get("/crm/segments").then((r) => setSegments(r.data));
  };
  useEffect(() => { load(); }, []);

  const openCustomer = async (email) => {
    setActive(email);
    const { data } = await api.get(`/crm/customers/${encodeURIComponent(email)}`);
    setDetail(data);
    setTags((data.tags || []).join(", "));
  };

  const saveTags = async () => {
    await api.post("/crm/tags", { customer_id: active, tags: tags.split(",").map(s => s.trim()).filter(Boolean) });
    toast.success("Tags saved");
    load();
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    await api.post("/crm/notes", { customer_id: active, note });
    setNote("");
    toast.success("Note added");
    const { data } = await api.get(`/crm/customers/${encodeURIComponent(active)}`);
    setDetail(data);
  };

  const createSegment = async (e) => {
    e.preventDefault();
    await api.post("/crm/segments", { ...newSegment, rules: {} });
    setNewSegment({ name: "", description: "" });
    toast.success("Segment created");
    load();
  };

  return (
    <DashboardLayout title="CRM" subtitle="Customers, tags, notes, and segments.">
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 v-card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="p-3">Customer</th>
                <th className="p-3 text-right">Orders</th>
                <th className="p-3 text-right">Spent</th>
                <th className="p-3">Last order</th>
                <th className="p-3">Tags</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.email} onClick={() => openCustomer(c.email)} className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer" data-testid={`crm-customer-${c.email}`}>
                  <td className="p-3">
                    <div className="text-primary font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{c.email}</div>
                  </td>
                  <td className="p-3 text-right font-mono">{c.orders}</td>
                  <td className="p-3 text-right font-mono">{formatGBP(c.spent)}</td>
                  <td className="p-3 text-xs">{formatDate(c.last_order)}</td>
                  <td className="p-3 text-xs">{(c.tags || []).map((t) => <span key={t} className="inline-block mr-1 px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary text-[10px] font-mono uppercase">{t}</span>)}</td>
                </tr>
              ))}
              {customers.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">No customers yet — they'll appear here after their first order.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="v-card">
          <h3 className="font-display text-lg font-semibold text-primary">Segments</h3>
          <form onSubmit={createSegment} className="mt-4 space-y-2">
            <input className="v-input" placeholder="Segment name (e.g. VIPs)" value={newSegment.name} onChange={(e) => setNewSegment({...newSegment, name: e.target.value})} required data-testid="segment-name" />
            <input className="v-input" placeholder="Description (optional)" value={newSegment.description} onChange={(e) => setNewSegment({...newSegment, description: e.target.value})} data-testid="segment-description" />
            <button className="v-btn-outline w-full" data-testid="segment-create">Create segment</button>
          </form>
          <div className="mt-4 space-y-2">
            {segments.map((s) => (
              <div key={s.id} className="border border-border rounded-sm p-3">
                <div className="font-medium text-primary text-sm">{s.name}</div>
                <div className="text-xs text-muted-foreground">{s.description}</div>
              </div>
            ))}
            {segments.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">No segments yet.</div>}
          </div>
        </div>
      </div>

      {active && detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-card max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-sm border border-border" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl font-bold text-primary">{detail.name}</h2>
                <div className="text-sm font-mono text-muted-foreground">{detail.email}</div>
                <div className="mt-2 flex gap-4 text-sm">
                  <span><strong>{detail.order_count}</strong> orders</span>
                  <span>{formatGBP(detail.spent)} lifetime</span>
                </div>
              </div>
              <button onClick={() => setActive(null)} className="v-btn-ghost"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <div className="label-eyebrow mb-2 flex items-center gap-1"><Tag size={12} /> Tags</div>
                <div className="flex gap-2">
                  <input className="v-input flex-1" placeholder="VIP, repeat-customer, …" value={tags} onChange={(e) => setTags(e.target.value)} data-testid="customer-tags-input" />
                  <button onClick={saveTags} className="v-btn-outline" data-testid="customer-tags-save">Save tags</button>
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-2 flex items-center gap-1"><MessageSquare size={12} /> Add a note</div>
                <textarea className="v-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="customer-note-input" />
                <button onClick={saveNote} className="v-btn-outline mt-2" data-testid="customer-note-save">Save note</button>
              </div>
              {detail.notes?.length > 0 && (
                <div>
                  <div className="label-eyebrow mb-2">Notes</div>
                  <div className="space-y-2">
                    {detail.notes.map((n) => (
                      <div key={n.id} className="border border-border rounded-sm p-3 text-sm">
                        <div className="text-foreground">{n.note}</div>
                        <div className="mt-1 text-[11px] text-muted-foreground font-mono">{n.author} · {formatDate(n.created_at)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="label-eyebrow mb-2">Recent orders</div>
                <div className="space-y-2">
                  {detail.orders?.slice(0, 8).map((o) => (
                    <div key={o.id} className="flex items-center justify-between text-sm border-b border-border py-2 last:border-0">
                      <span className="font-mono text-xs">{o.order_number}</span>
                      <span>{formatDate(o.created_at)}</span>
                      <span className="font-mono">{formatGBP(o.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
